import { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, Loader2, Home, Phone, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  // loading | success | error | pending (verified but not yet paid — e.g. timeout with unpaid status)
  const [status, setStatus] = useState('loading');
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 8;
    const pollInterval = 2000;

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);

      if (response.data.payment_status === 'paid') {
        // Only real confirmation from Stripe is a "success"
        setStatus('success');
        return;
      }
      if (response.data.status === 'expired') {
        setStatus('error');
        return;
      }

      attemptsRef.current += 1;
      if (attemptsRef.current >= maxAttempts) {
        // Timed out without a paid confirmation — do NOT claim success
        setStatus('pending');
        return;
      }
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      attemptsRef.current += 1;
      if (attemptsRef.current >= maxAttempts) {
        // Network/API failure and still no confirmation — surface it, do NOT fake success
        setStatus('error');
        return;
      }
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  return (
    <div className="pt-20 min-h-screen bg-[#F5F2EA] flex items-center justify-center" data-testid="payment-success-page">
      <div className="max-w-lg mx-auto px-4 text-center">
        <div className="bg-white rounded-2xl p-12 shadow-lg">
          {status === 'loading' && (
            <>
              <Loader2 className="w-20 h-20 text-[#C62E2E] mx-auto mb-6 animate-spin" />
              <h2 className="font-['Playfair_Display'] text-3xl font-bold text-[#1C1917] mb-4">
                Vérification du paiement...
              </h2>
              <p className="text-[#57534E]">
                Veuillez patienter pendant que nous confirmons votre paiement.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 rounded-full bg-[#3A6351]/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-[#3A6351]" />
              </div>
              <h2 className="font-['Playfair_Display'] text-3xl font-bold text-[#1C1917] mb-4">
                Commande confirmée !
              </h2>
              <p className="text-[#57534E] mb-4">
                Merci pour votre commande ! Nous préparons vos plats avec soin.
              </p>
              <div className="bg-[#FEF3C7] border border-[#F59E0B]/30 rounded-xl p-4 mb-6 text-left">
                <p className="text-[#78350F] text-sm font-semibold mb-1">
                  📧 Vérifiez vos emails (et vos spams)
                </p>
                <p className="text-[#78350F] text-sm">
                  Un email de confirmation vous a été envoyé avec le récapitulatif de votre commande. Pensez à le consulter <strong>absolument</strong>.
                </p>
              </div>
              
              <div className="bg-[#FAF9F6] rounded-xl p-6 mb-8">
                <p className="text-[#57534E] text-sm mb-2">
                  Pour toute question concernant votre commande :
                </p>
                <a 
                  href="tel:+3265950649" 
                  className="flex items-center justify-center gap-2 text-[#C62E2E] font-semibold"
                >
                  <Phone className="w-5 h-5" />
                  065 95 06 49
                </a>
              </div>

              <Link to="/">
                <Button
                  className="bg-[#C62E2E] hover:bg-[#a02424] text-white rounded-full px-8"
                  data-testid="back-to-home"
                >
                  <Home className="w-5 h-5 mr-2" />
                  Retour à l'accueil
                </Button>
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-[#C62E2E]" />
              </div>
              <h2 className="font-['Playfair_Display'] text-3xl font-bold text-[#1C1917] mb-4">
                Erreur de paiement
              </h2>
              <p className="text-[#57534E] mb-8">
                Le paiement n'a pas pu être traité. Veuillez réessayer ou nous contacter.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/commander">
                  <Button
                    className="bg-[#C62E2E] hover:bg-[#a02424] text-white rounded-full px-8"
                    data-testid="retry-order"
                  >
                    Réessayer
                  </Button>
                </Link>
                <a href="tel:+3265950649">
                  <Button
                    variant="outline"
                    className="rounded-full px-8"
                  >
                    Appeler: 065 95 06 49
                  </Button>
                </a>
              </div>
            </>
          )}

          {status === 'pending' && (
            <>
              <div className="w-20 h-20 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-6">
                <Clock className="w-10 h-10 text-amber-600" />
              </div>
              <h2 className="font-['Playfair_Display'] text-3xl font-bold text-[#1C1917] mb-4" data-testid="payment-pending-title">
                Paiement en cours de vérification
              </h2>
              <p className="text-[#57534E] mb-4">
                Nous n'avons pas encore reçu la confirmation de votre paiement.
                Ne relancez pas la commande — nous vous confirmerons par email dès que le paiement sera validé.
              </p>
              <p className="text-[#57534E] mb-8 text-sm">
                En cas de doute, appelez-nous avec votre référence de paiement.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:+3265950649">
                  <Button className="bg-[#C62E2E] hover:bg-[#a02424] text-white rounded-full px-8">
                    <Phone className="w-4 h-4 mr-2" /> 065 95 06 49
                  </Button>
                </a>
                <Link to="/">
                  <Button variant="outline" className="rounded-full px-8">
                    <Home className="w-4 h-4 mr-2" /> Accueil
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
